// ============================================================================
// LOBBY BROWSER SERVICE - Find and list public game lobbies
// ============================================================================
// Allows players to browse and join public games without a room code

import { FIREBASE_CONFIG } from '../config/firebase.config';

export interface PublicLobby {
  roomCode: string;
  hostName: string;
  hostId: string;
  gameMode: 'deathmatch' | 'spike';
  mapId: string;
  currentPlayers: number;
  maxPlayers: number;
  createdAt: number;
  isPublic: boolean;
  status: 'waiting' | 'starting' | 'playing' | 'finished';
}

class LobbyBrowserServiceClass {
  private db: any = null;
  private firebase: any = null;
  private isInitialized = false;
  private unsubscribe: (() => void) | null = null;

  // Initialize Firebase connection
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      const { initializeApp, getApps, getApp } = await import('firebase/app');
      const { 
        getFirestore, collection, doc, setDoc, getDoc, updateDoc, deleteDoc,
        query, where, orderBy, limit, getDocs, onSnapshot, serverTimestamp
      } = await import('firebase/firestore');

      this.firebase = { 
        collection, doc, setDoc, getDoc, updateDoc, deleteDoc,
        query, where, orderBy, limit, getDocs, onSnapshot, serverTimestamp
      };

      const app = getApps().length === 0 ? initializeApp(FIREBASE_CONFIG) : getApp();
      this.db = getFirestore(app);

      this.isInitialized = true;
      console.log('[LobbyBrowser] Initialized');
    } catch (e) {
      console.error('[LobbyBrowser] Initialization failed:', e);
    }
  }

  // Create or update a public lobby listing
  async createPublicLobby(params: {
    roomCode: string;
    hostId: string;
    hostName: string;
    gameMode: 'deathmatch' | 'spike';
    mapId: string;
    maxPlayers: number;
  }): Promise<void> {
    if (!this.isInitialized) await this.initialize();

    const { doc, setDoc, serverTimestamp } = this.firebase;

    try {
      const lobbyRef = doc(this.db, 'publicLobbies', params.roomCode);
      await setDoc(lobbyRef, {
        roomCode: params.roomCode,
        hostId: params.hostId,
        hostName: params.hostName,
        gameMode: params.gameMode,
        mapId: params.mapId,
        currentPlayers: 1,
        maxPlayers: params.maxPlayers,
        isPublic: true,
        status: 'waiting',
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      });

      console.log(`[LobbyBrowser] Public lobby created: ${params.roomCode}`);
    } catch (e) {
      console.error('[LobbyBrowser] Failed to create public lobby:', e);
    }
  }

  // Update player count in a public lobby
  async updateLobbyPlayerCount(roomCode: string, currentPlayers: number): Promise<void> {
    if (!this.isInitialized) await this.initialize();

    const { doc, updateDoc, serverTimestamp } = this.firebase;

    try {
      const lobbyRef = doc(this.db, 'publicLobbies', roomCode);
      await updateDoc(lobbyRef, {
        currentPlayers,
        lastUpdated: serverTimestamp(),
      });
    } catch (e) {
      console.warn('[LobbyBrowser] Failed to update player count:', e);
    }
  }

  // Update lobby status (waiting, starting, playing, finished)
  async updateLobbyStatus(roomCode: string, status: PublicLobby['status']): Promise<void> {
    if (!this.isInitialized) await this.initialize();

    const { doc, updateDoc, deleteDoc, serverTimestamp } = this.firebase;

    try {
      const lobbyRef = doc(this.db, 'publicLobbies', roomCode);
      
      if (status === 'playing' || status === 'finished') {
        // Remove from public listings when game starts or ends
        await deleteDoc(lobbyRef);
        console.log(`[LobbyBrowser] Lobby removed: ${roomCode}`);
      } else {
        await updateDoc(lobbyRef, {
          status,
          lastUpdated: serverTimestamp(),
        });
      }
    } catch (e) {
      console.warn('[LobbyBrowser] Failed to update lobby status:', e);
    }
  }

  // Remove a public lobby
  async removeLobby(roomCode: string): Promise<void> {
    if (!this.isInitialized) await this.initialize();

    const { doc, deleteDoc } = this.firebase;

    try {
      const lobbyRef = doc(this.db, 'publicLobbies', roomCode);
      await deleteDoc(lobbyRef);
      console.log(`[LobbyBrowser] Lobby removed: ${roomCode}`);
    } catch (e) {
      console.warn('[LobbyBrowser] Failed to remove lobby:', e);
    }
  }

  // Fetch list of available public lobbies
  async getPublicLobbies(): Promise<PublicLobby[]> {
    if (!this.isInitialized) await this.initialize();

    const { collection, query, limit, getDocs } = this.firebase;

    try {
      console.log('[LobbyBrowser] Querying publicLobbies collection...');
      
      // Simpler query - filter in code to avoid composite index requirement
      const q = query(
        collection(this.db, 'publicLobbies'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      console.log(`[LobbyBrowser] Query returned ${snapshot.size} documents`);
      
      const lobbies: PublicLobby[] = [];

      snapshot.forEach((doc: any) => {
        const data = doc.data();
        console.log('[LobbyBrowser] Lobby data:', data);
        
        // Filter for waiting, public lobbies that aren't full
        if (data.status === 'waiting' && 
            data.isPublic === true && 
            data.currentPlayers < data.maxPlayers) {
          lobbies.push({
            roomCode: data.roomCode,
            hostName: data.hostName,
            hostId: data.hostId,
            gameMode: data.gameMode,
            mapId: data.mapId,
            currentPlayers: data.currentPlayers,
            maxPlayers: data.maxPlayers,
            createdAt: data.createdAt?.toMillis?.() || Date.now(),
            isPublic: data.isPublic,
            status: data.status,
          });
        }
      });

      // Sort by creation time (newest first)
      lobbies.sort((a, b) => b.createdAt - a.createdAt);

      console.log(`[LobbyBrowser] Found ${lobbies.length} public lobbies`);
      return lobbies;
    } catch (e) {
      console.error('[LobbyBrowser] Failed to fetch lobbies:', e);
      return [];
    }
  }

  // Subscribe to real-time lobby updates
  subscribeToLobbies(callback: (lobbies: PublicLobby[]) => void): () => void {
    if (!this.isInitialized) {
      console.warn('[LobbyBrowser] Not initialized - call initialize() first');
      return () => {};
    }
    
    // Clean up existing subscription if any
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    const { collection, query, limit, onSnapshot } = this.firebase;

    try {
      console.log('[LobbyBrowser] Setting up real-time subscription...');
      
      // Simpler query - filter in code to avoid composite index requirement
      const q = query(
        collection(this.db, 'publicLobbies'),
        limit(50)
      );

      const unsubscribe = onSnapshot(q, (snapshot: any) => {
        const lobbies: PublicLobby[] = [];

        snapshot.forEach((doc: any) => {
          const data = doc.data();
          // Filter for waiting, public lobbies that aren't full
          if (data.status === 'waiting' && 
              data.isPublic === true && 
              data.currentPlayers < data.maxPlayers) {
            lobbies.push({
              roomCode: data.roomCode,
              hostName: data.hostName,
              hostId: data.hostId,
              gameMode: data.gameMode,
              mapId: data.mapId,
              currentPlayers: data.currentPlayers,
              maxPlayers: data.maxPlayers,
              createdAt: data.createdAt?.toMillis?.() || Date.now(),
              isPublic: data.isPublic,
              status: data.status,
            });
          }
        });

        // Sort by creation time (newest first)
        lobbies.sort((a, b) => b.createdAt - a.createdAt);
        
        callback(lobbies);
      }, (error: any) => {
        console.error('[LobbyBrowser] Subscription error:', error);
      });

      this.unsubscribe = unsubscribe;
      return unsubscribe;
    } catch (e) {
      console.error('[LobbyBrowser] Failed to subscribe to lobbies:', e);
      return () => {};
    }
  }

  // Stop listening for lobby updates
  unsubscribeFromLobbies(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  // Clean up stale lobbies (older than 10 minutes)
  async cleanupStaleLobbies(): Promise<number> {
    if (!this.isInitialized) await this.initialize();

    const { collection, query, getDocs, deleteDoc, doc, limit } = this.firebase;

    try {
      const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
      
      // Simple query, filter in code
      const q = query(
        collection(this.db, 'publicLobbies'),
        limit(100)
      );

      const snapshot = await getDocs(q);
      let cleaned = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const createdAt = data.createdAt?.toMillis?.() || 0;
        
        if (createdAt < tenMinutesAgo && data.status === 'waiting') {
          await deleteDoc(doc(this.db, 'publicLobbies', docSnap.id));
          cleaned++;
        }
      }

      if (cleaned > 0) {
        console.log(`[LobbyBrowser] Cleaned up ${cleaned} stale lobbies`);
      }

      return cleaned;
    } catch (e) {
      console.warn('[LobbyBrowser] Failed to cleanup stale lobbies:', e);
      return 0;
    }
  }
}

// Singleton instance
export const LobbyBrowserService = new LobbyBrowserServiceClass();
